// Dashboard.jsx — Fully Responsive Wizard Carousel (container-ref approach)
//
// ROOT CAUSE of previous bugs:
//   - Card widths were hardcoded (260px, 280px) ignoring the real container width.
//   - On screens < 1400px the sidebar + padding shrinks the content area, so
//     fixed card widths made the last steps overflow and become unreachable.
//   - On mobile the card was wider than the available space, clipping text.
//
// FIX STRATEGY:
//   - Attach a ResizeObserver to the carousel track <div> to measure its REAL pixel width.
//   - cardWidth = (trackWidth - GAP * (visibleCount - 1)) / visibleCount
//   - This guarantees cards always fill the container exactly at every viewport/sidebar combo.
//   - visibleCount is determined by viewport breakpoints (1 / 2 / 3 / 4).
//   - MAX_STEP_INDEX = WIZARD_STEPS.length - visibleCount  → step 7 always reachable.

import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import axiosInstance from "@/config/axiosInstance";
import { Sidebar } from "@/components/Sidebar";
import { PageHeader } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader,
  DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { useToast } from "@/components/ui/use-toast";
import { cn } from "@/lib/utils";
import {
  Sparkles,
  Target,
  LayoutGrid,
  Calendar,
  CheckCircle,
  TrendingUp,
  Mail,
  Lightbulb,
  Lock,
  ChevronLeft,
  ChevronRight,
  HelpCircle,
  Bell,
  User,
  LogOut,
  FolderKanban,
  Clock,
  Send,
  MousePointer,
  Eye,
  Menu,
  X,
  LifeBuoy,
  Rocket,
  BarChart3,
  Users,
  Palette,
  Loader,
} from "lucide-react";
import { endpoints } from "@/config/apiEndpoints";
import { useTimezone } from "@/contexts/TimezoneContext";
// import Chatbot from "@/components/Chatbot";
// import CallingBot from "@/components/Callingbot";
import BotLauncher from "@/components/BotLauncher";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
// ─────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────
interface UserData {
  id: string; email: string; first_name?: string; last_name?: string;
  name: string; avatar?: string; plan_name: string;
  campaigns_remaining: number; show_notification_opt_in_popup?: boolean;
}
interface Campaign {
  id?: string; _id?: string; campaign_name?: string; property_title?: string;
  campaign_type?: string; service_type?: string; status?: string;
  date?: string; created?: string; created_at?: string; scheduled_date?: string; sent_at?: string;
  scheduled_timezone?: string; campaign_subject?: string;
  timesrun?: number; sent?: number; openrate?: number; opens?: number; open_count?: number;
  clickrate?: number; clicks?: number; click_count?: number; recipients_count?: number; recipient_count?: number;
}
interface WizardStep { number: number; icon: React.ElementType; title: string; description: string; }
interface ChartDataPoint { label: string; value: number | null; isToday?: boolean; }

// ─────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────
const WIZARD_STEPS: WizardStep[] = [
  { number: 1, icon: Target, title: "Pick Your Launch", description: "Tell us what you're marketing and we'll do the rest." },
  { number: 2, icon: LayoutGrid, title: "Tell Us About Your Launch", description: "Bring your Launch to life—import files, drag & drop, or describe it your way." },
  { number: 3, icon: Palette, title: "Pick Your Look", description: "Choose a template, customize the look—or let AI do the work. Preview your design instantly." },
  { number: 4, icon: Users, title: "Choose Your Hub", description: "Choose recipient groups and add your My Hub contacts to boost Launch results." },
  { number: 5, icon: Calendar, title: "Schedule Your Launch", description: "Choose your launch time or automate recurring Launches for better performance." },
  { number: 6, icon: CheckCircle, title: "You're All Set to Launch", description: "Your Launch is ready. Complete checkout to launch." },
  { number: 7, icon: BarChart3, title: "See Your Results", description: "Get an email at launch and a follow-up with click results. Pro and Pro+ users also receive SMS alerts." },
];

const ALL_DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const SMART_ACTIONS = [
  { label: "Support", route: "/contact", icon: Clock, requiresPro: false },
  { label: "LaunchIQ Builder", route: "/builder", icon: Rocket, requiresPro: false },
  { label: "LaunchIQ Management", route: "/launches", icon: LayoutGrid, requiresPro: false },
  { label: "My Hub", route: "/agent", icon: Users, requiresPro: true },
];

const PRO_PLANS = ["Pro", "Pro+", "Pro Annual Plan", "Pro+ Annual Plan"];

// ─────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────
const getCurrentDayIndex = () => { const d = new Date().getDay(); return d === 0 ? 6 : d - 1; };

// ─────────────────────────────────────────────────────────────────
// useWindowWidth
// ─────────────────────────────────────────────────────────────────
const useWindowWidth = () => {
  const [w, setW] = useState(typeof window !== "undefined" ? window.innerWidth : 1200);
  useEffect(() => {
    const cb = () => setW(window.innerWidth);
    window.addEventListener("resize", cb);
    return () => window.removeEventListener("resize", cb);
  }, []);
  return w;
};

// ─────────────────────────────────────────────────────────────────
// DynamicAreaChart
// ─────────────────────────────────────────────────────────────────
const DynamicAreaChart: React.FC<{ data: ChartDataPoint[]; color: string; showValues?: boolean }> = ({
  data, color, showValues = true,
}) => {
  if (!data.length)
    return (
      <svg viewBox="0 0 320 96" className="w-full h-full" preserveAspectRatio="xMidYMid meet">
        <text x="160" y="50" fontSize="12" fill="#9CA3AF" textAnchor="middle">No data available</text>
      </svg>
    );

  const nums = data.map((d) => d.value).filter((v) => v !== null) as number[];
  const minV = nums.length ? Math.min(...nums) : 0;
  const maxV = nums.length ? Math.max(...nums, 1) : 1;
  const allSame = nums.length <= 1 || minV === maxV;
  const isFlat = allSame && minV === 0;
  const chartH = isFlat ? 30 : 52;
  const topPad = isFlat ? 15 : 18;
  const botY = isFlat ? 50 : 78;
  const labY = isFlat ? 62 : 92;
  const effMax = allSame ? maxV * 2 : maxV;
  const n = data.length;
  const sp0 = n === 7 ? 45 : 60;
  const cw = Math.min(400, Math.max(280, 50 + Math.max(1, n - 1) * sp0));
  const sp = n > 1 ? (cw - 50) / (n - 1) : sp0;

  let pathD = "";
  const pts: { x: number; y?: number; val: number | null; label: string; isToday: boolean }[] = [];
  data.forEach((d, i) => {
    const x = 25 + i * sp;
    if (d.value === null || d.value === undefined) { pts.push({ x, val: null, label: d.label, isToday: !!d.isToday }); return; }
    const y = allSame ? topPad + chartH / 2 : topPad + (1 - (d.value - minV) / (effMax - minV)) * chartH;
    pts.push({ x, y, val: d.value, label: d.label, isToday: !!d.isToday });
    pathD += `${pathD === "" ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)} `;
  });
  const real = pts.filter((p) => p.val !== null && p.y !== undefined);
  const areaPath = real.length && pathD
    ? `${pathD} L${real[real.length - 1].x.toFixed(1)},${botY} L${real[0].x.toFixed(1)},${botY} Z` : "";
  const gid = `g${color.replace("#", "")}`;

  return (
    <svg viewBox={`0 0 ${cw} ${isFlat ? 80 : 110}`} className="w-full h-full" preserveAspectRatio="xMidYMid meet">
      <defs>
        <linearGradient id={gid} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0.8" />
        </linearGradient>
      </defs>
      {areaPath && <path d={areaPath} fill={`url(#${gid})`} opacity="0.3" />}
      {pathD && <path d={pathD} fill="none" stroke={color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />}
      {pts.map((p, i) => p.val !== null && p.y !== undefined ? (
        <g key={i}>
          {p.isToday && <circle cx={p.x} cy={p.y} r="8" fill={color} opacity="0.2" />}
          <circle cx={p.x} cy={p.y} r={p.isToday ? 5 : 4} fill={color} />
          {showValues && p.val > 0 && <text x={p.x} y={p.y - 10} fontSize="11" fill={color} textAnchor="middle" fontWeight="bold">{p.val}</text>}
        </g>
      ) : null)}
      {pts.map((p, i) => (
        <text key={`l${i}`} x={p.x} y={labY} fontSize={n > 4 ? "8" : "9"} fill={p.isToday ? color : "#6B7280"} textAnchor="middle" fontWeight={p.isToday ? "bold" : "normal"}>
          {p.label}{p.isToday ? " •" : ""}
        </text>
      ))}
    </svg>
  );
};

// ─────────────────────────────────────────────────────────────────
// WizardStepCard — width passed as prop (computed from real container)
// ─────────────────────────────────────────────────────────────────
const WizardStepCard: React.FC<{ step: WizardStep; cardWidth: number }> = ({ step, cardWidth }) => (
  <div className="flex-shrink-0" style={{ width: cardWidth }}>
    {/* pt-6 gives room for the number badge sitting at the top */}
    <div className="pt-6 h-full">
      <div className="
        bg-white rounded-2xl border border-gray-200
        p-4 sm:p-5
        flex flex-col items-center text-center
        transition-all duration-300
        hover:shadow-xl hover:-translate-y-1 hover:scale-[1.02]
        h-full min-h-[210px]
      ">
        {/* Number badge */}
        <div className="w-10 h-10 bg-blue-600 text-white font-bold text-base rounded-full flex items-center justify-center shadow-lg mb-3 flex-shrink-0">
          {step.number}
        </div>
        {/* Icon */}
        <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center mb-3 flex-shrink-0">
          <step.icon className="w-6 h-6 text-blue-600" />
        </div>
        {/* Title */}
        <h4 className="text-sm sm:text-base font-bold text-gray-900 mb-2 leading-snug w-full">
          {step.title}
        </h4>
        {/* Description — wraps naturally, never truncated */}
        <p className="text-xs sm:text-sm text-gray-500 leading-relaxed w-full">
          {step.description}
        </p>
      </div>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────
// Dashboard
// ─────────────────────────────────────────────────────────────────
const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const vw = useWindowWidth();
  const { timezone } = useTimezone();

  // ── Helper: Format date/time in user's timezone ────────────────────────────
  const formatDateTimeInTimezone = useCallback((value?: string | null, tz?: string) => {
    if (!value) return "";
    const targetTz = tz || timezone || "UTC";
    try {
      return new Intl.DateTimeFormat(undefined, {
        timeZone: targetTz,
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      }).format(new Date(value));
    } catch {
      return value;
    }
  }, [timezone]);

  const [user, setUser] = useState<UserData | null>(null);
  const [userLoading, setUserLoading] = useState(true);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showNotificationOptInModal, setShowNotificationOptInModal] = useState(false);
  const [wizardStep, setWizardStep] = useState(0);
  const [timeRange, setTimeRange] = useState<"30d" | "thisWeek" | "lastWeek">("30d");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [mobileMenuToggle, setMobileMenuToggle] = useState<(() => void) | null>(null);

  // ── Carousel: measure real track width via ResizeObserver ────────────────
  const trackRef = useRef<HTMLDivElement>(null);
  const [trackW, setTrackW] = useState(0);

  useEffect(() => {
    if (!trackRef.current) return;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) setTrackW(e.contentRect.width);
    });
    ro.observe(trackRef.current);
    setTrackW(trackRef.current.getBoundingClientRect().width);
    return () => ro.disconnect();
  }, []);

  // ── Visible card count by viewport width ────────────────────────────────
  const visibleCount = useMemo(() => {
    if (vw < 640) return 1;
    if (vw < 900) return 2;
    if (vw < 1280) return 3;
    return 4;
  }, [vw]);

  const GAP = 16; // px, same on all sizes
  const MAX_STEP = Math.max(0, WIZARD_STEPS.length - visibleCount);

  // ── Card width: fills the track exactly, no overflow ────────────────────
  const cardWidth = useMemo(() => {
    if (!trackW) return 200; // pre-measure fallback
    return Math.max(140, Math.floor((trackW - GAP * (visibleCount - 1)) / visibleCount));
  }, [trackW, visibleCount]);

  const STEP_MOVE = cardWidth + GAP;

  // Clamp step index when visibleCount changes (e.g. on resize)
  useEffect(() => { setWizardStep((p) => Math.min(p, MAX_STEP)); }, [MAX_STEP]);

  const goPrev = useCallback(() => setWizardStep((p) => Math.max(0, p - 1)), []);
  const goNext = useCallback(() => setWizardStep((p) => Math.min(MAX_STEP, p + 1)), [MAX_STEP]);

  const hasProAccess = PRO_PLANS.includes(user?.plan_name ?? "");

  const getCampaignStatus = useCallback((campaign: Campaign) => {
    const rawStatus = String(campaign?.status || "").trim().toLowerCase();
    if (rawStatus) return rawStatus;

    const scheduledDate = campaign?.scheduled_date || campaign?.date;
    if (scheduledDate && new Date(scheduledDate).getTime() > Date.now()) {
      return "scheduled";
    }

    return "pending";
  }, []);

  const normalizeCampaign = useCallback((campaign: any): Campaign => {
    const recipients =
      Number(campaign?.recipient_count ?? campaign?.recipients_count ?? campaign?.recipients ?? campaign?.sent ?? 0) || 0;

    return {
      ...campaign,
      id: campaign?.id ?? campaign?.campaign_id ?? campaign?.property_id,
      status: getCampaignStatus(campaign),
      recipients_count: recipients,
      recipient_count: recipients,
      date: campaign?.date || campaign?.created || campaign?.created_at || campaign?.scheduled_date || null,
      scheduled_date: campaign?.scheduled_date || campaign?.date || null,
      open_count: Number(campaign?.open_count ?? campaign?.opens ?? 0) || 0,
      click_count: Number(campaign?.click_count ?? campaign?.clicks ?? 0) || 0,
      openrate: Number(campaign?.openrate ?? 0) || 0,
      clickrate: Number(campaign?.clickrate ?? 0) || 0,
      timesrun: Number(campaign?.timesrun ?? 1) || 1,
    };
  }, [getCampaignStatus]);

  const isSentCampaign = useCallback((campaign: Campaign) => {
    const status = getCampaignStatus(campaign);
    return status === "sent" || status === "completed";
  }, [getCampaignStatus]);

  const getCampaignRecipients = useCallback((campaign: Campaign) =>
    Number(campaign.recipients_count ?? campaign.recipient_count ?? campaign.sent ?? 0) || 0,
  []);

  const getCampaignOpenCount = useCallback((campaign: Campaign) => {
    const explicit = Number(campaign.open_count ?? campaign.opens ?? 0) || 0;
    if (explicit > 0) return explicit;

    const rate = Number(campaign.openrate ?? 0) || 0;
    const normalizedRate = rate > 100 ? rate / 10 : rate;
    return Math.round((normalizedRate / 100) * getCampaignRecipients(campaign));
  }, [getCampaignRecipients]);

  const getCampaignClickCount = useCallback((campaign: Campaign) => {
    const explicit = Number(campaign.click_count ?? campaign.clicks ?? 0) || 0;
    if (explicit > 0) return explicit;

    const rate = Number(campaign.clickrate ?? 0) || 0;
    const normalizedRate = rate > 100 ? rate / 10 : rate;
    return Math.round((normalizedRate / 100) * getCampaignRecipients(campaign));
  }, [getCampaignRecipients]);

  const getStatusLabel = useCallback((status?: string) => {
    const normalized = String(status || "pending").toLowerCase();
    return normalized.charAt(0).toUpperCase() + normalized.slice(1);
  }, []);

  const getStatusBadgeClass = useCallback((status?: string) => {
    switch (String(status || "").toLowerCase()) {
      case "running":
        return "bg-emerald-100 text-emerald-700";
      case "scheduled":
        return "bg-green-100 text-green-700";
      case "pending":
        return "bg-amber-100 text-amber-700";
      case "sent":
      case "completed":
        return "bg-blue-100 text-blue-700";
      case "failed":
      case "error":
        return "bg-red-100 text-red-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  }, []);

  const extractCampaignList = useCallback((data: any): any[] => {
    // Same logic as CampaignManagement - works correctly
    const result = Array.isArray(data)
      ? data
      : Array.isArray(data?.campaigns)
        ? data.campaigns
        : [];
    console.log("✅ Dashboard extracted", result.length, "campaigns from API");
    return result;
  }, []);

  // ── Chart data ───────────────────────────────────────────────────────────
  const thisWeekChartData = useMemo(() => {
    const todayIdx = getCurrentDayIndex();
    const now = new Date();
    const dow = now.getDay();
    const monday = new Date(now);
    monday.setDate(now.getDate() + (dow === 0 ? -6 : 1 - dow));
    monday.setHours(0, 0, 0, 0);
    const days = Array(7).fill(null).map(() => ({ sent: 0, hubs: 0, clicks: 0 }));
    campaigns.forEach((c) => {
      const ds = c.sent_at || c.date || c.created_at;
      if (!ds) return;
      const d = new Date(ds);
      if (isNaN(d.getTime()) || d < monday || d > now) return;
      const idx = d.getDay() === 0 ? 6 : d.getDay() - 1;
      if (isSentCampaign(c)) {
        days[idx].sent += 1;
        days[idx].hubs += (c.timesrun || 1) * getCampaignRecipients(c);
        days[idx].clicks += getCampaignClickCount(c);
      }
    });
    return {
      sent: ALL_DAY_LABELS.map((label, i) => ({ label, value: days[i].sent, isToday: i === todayIdx })),
      hubs: ALL_DAY_LABELS.map((label, i) => ({ label, value: days[i].hubs, isToday: i === todayIdx })),
      clicks: ALL_DAY_LABELS.map((label, i) => ({ label, value: days[i].clicks, isToday: i === todayIdx })),
    };
  }, [campaigns, getCampaignClickCount, getCampaignRecipients, isSentCampaign]);

  const lastWeekChartData = useMemo(() => {
    const now = new Date(); const dow = now.getDay();
    const lm = new Date(now); lm.setDate(now.getDate() + (dow === 0 ? -13 : 1 - dow - 7)); lm.setHours(0, 0, 0, 0);
    const ls = new Date(lm); ls.setDate(lm.getDate() + 6); ls.setHours(23, 59, 59, 999);
    const days = Array(7).fill(null).map(() => ({ sent: 0, hubs: 0, clicks: 0 }));
    campaigns.forEach((c) => {
      const ds = c.sent_at || c.date || c.created_at; if (!ds) return;
      const d = new Date(ds); if (isNaN(d.getTime()) || d < lm || d > ls) return;
      const idx = d.getDay() === 0 ? 6 : d.getDay() - 1;
      if (isSentCampaign(c)) {
        days[idx].sent += 1; days[idx].hubs += (c.timesrun || 1) * getCampaignRecipients(c); days[idx].clicks += getCampaignClickCount(c);
      }
    });
    return {
      sent: ALL_DAY_LABELS.map((label, i) => ({ label, value: days[i].sent })),
      hubs: ALL_DAY_LABELS.map((label, i) => ({ label, value: days[i].hubs })),
      clicks: ALL_DAY_LABELS.map((label, i) => ({ label, value: days[i].clicks })),
    };
  }, [campaigns, getCampaignClickCount, getCampaignRecipients, isSentCampaign]);

  const thirtyDaysChartData = useMemo(() => {
    const now = new Date(); now.setHours(23, 59, 59, 999);
    const ago = new Date(now); ago.setDate(now.getDate() - 30); ago.setHours(0, 0, 0, 0);
    const p = [{ sent: 0, hubs: 0, clicks: 0 }, { sent: 0, hubs: 0, clicks: 0 }, { sent: 0, hubs: 0, clicks: 0 }];
    campaigns.forEach((c) => {
      const ds = c.sent_at || c.date || c.created_at; if (!ds) return;
      const d = new Date(ds); if (isNaN(d.getTime()) || d < ago || d > now) return;
      const da = Math.floor((now.getTime() - d.getTime()) / 86400000);
      const pi = da <= 10 ? 0 : da <= 20 ? 1 : 2;
      if (isSentCampaign(c)) {
        p[pi].sent += 1; p[pi].hubs += (c.timesrun || 1) * getCampaignRecipients(c); p[pi].clicks += getCampaignClickCount(c);
      }
    });
    const lbs = ["Days 21-30", "Days 11-20", "Days 1-10"];
    return {
      sent: [{ label: lbs[0], value: p[2].sent }, { label: lbs[1], value: p[1].sent }, { label: lbs[2], value: p[0].sent }],
      hubs: [{ label: lbs[0], value: p[2].hubs }, { label: lbs[1], value: p[1].hubs }, { label: lbs[2], value: p[0].hubs }],
      clicks: [{ label: lbs[0], value: p[2].clicks }, { label: lbs[1], value: p[1].clicks }, { label: lbs[2], value: p[0].clicks }],
    };
  }, [campaigns, getCampaignClickCount, getCampaignRecipients, isSentCampaign]);

  const remainingChartData = useMemo(() => {
    const cur = user?.campaigns_remaining ?? 0;
    if (timeRange === "thisWeek") {
      const ti = getCurrentDayIndex();
      const tot = thisWeekChartData.sent.reduce((s, d) => s + (d.value || 0), 0);
      let rem = cur + tot;
      return ALL_DAY_LABELS.map((label, i) => {
        const s = thisWeekChartData.sent[i]?.value || 0;
        if (i <= ti) { rem -= s; return { label, value: Math.max(0, rem), isToday: i === ti }; }
        return { label, value: rem, isToday: false };
      });
    }
    if (timeRange === "lastWeek") return ALL_DAY_LABELS.map((label) => ({ label, value: cur }));
    const t30 = thirtyDaysChartData.sent.reduce((s, d) => s + d.value, 0);
    let rem = cur + t30;
    return thirtyDaysChartData.sent.map((d) => { rem -= d.value; return { label: d.label, value: Math.max(0, rem) }; });
  }, [user, timeRange, thisWeekChartData, thirtyDaysChartData]);

  const chartData = useMemo(() => {
    if (timeRange === "thisWeek") return thisWeekChartData;
    if (timeRange === "lastWeek") return lastWeekChartData;
    return thirtyDaysChartData;
  }, [timeRange, thisWeekChartData, lastWeekChartData, thirtyDaysChartData]);

  const periodStats = useMemo(() => ({
    campaignsSent: chartData.sent.reduce((s, d) => s + (d.value || 0), 0),
    hubs: chartData.hubs.reduce((s, d) => s + (d.value || 0), 0),
    clicks: chartData.clicks.reduce((s, d) => s + (d.value || 0), 0),
  }), [chartData]);

  const periodText = timeRange === "30d" ? "This Month" : timeRange === "thisWeek" ? "This Week" : "Last Week";
  const recentCampaigns = useMemo(() => campaigns.slice(0, 5), [campaigns]);
  const getCampaignName = useCallback((c: Campaign) =>
    c.campaign_name?.trim() || c.property_title?.trim() || (c.campaign_type?.toLowerCase() === "service" ? c.service_type || "Service Launch" : "Untitled Launch"),
    []);
  const firstName = useMemo(() => { if (!user?.name) return "there"; return user.name.trim().split(" ")[0]; }, [user]);

  // ── Fetch ────────────────────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      try {
        setUserLoading(true); setLoading(true);
        console.log("🔄 Dashboard: Starting fetch of user and campaigns...");
        
        const [userResult, campaignResult] = await Promise.allSettled([
          axiosInstance.get(endpoints.me),
          axiosInstance.get(endpoints.listCampaigns),
        ]);

        if (userResult.status === "fulfilled") {
          const ud = userResult.value.data; const plan = ud.plan_name || ud.plan || "Free";
          const showNotif = PRO_PLANS.includes(plan) && ud.show_notification_opt_in_popup === true;
          console.log("👤 User loaded:", ud.name || ud.first_name);
          setUser({
            id: ud.id || ud.pk || ud._id || "", email: ud.email, first_name: ud.first_name, last_name: ud.last_name,
            name: ud.name || `${ud.first_name || ""} ${ud.last_name || ""}`.trim(),
            plan_name: plan, campaigns_remaining: ud.campaigns_remaining ?? 0,
            show_notification_opt_in_popup: ud.show_notification_opt_in_popup
          });
          if (showNotif) { setShowNotificationOptInModal(true); axiosInstance.post(endpoints.notificationOptInPopupShown).catch(console.error); }
        } else {
          console.error("Dashboard user load failed:", userResult.reason);
          setUser(null);
        }

        if (campaignResult.status !== "fulfilled") {
          console.error("❌ Campaign fetch failed:", campaignResult.reason);
          throw campaignResult.reason;
        }

        console.log("📡 Campaign API response received, extracting data...");
        const campaignData = extractCampaignList(campaignResult.value.data);
        console.log("✅ Extracted", campaignData.length, "campaigns from API response");
        
        const normalizedCampaigns = campaignData
          .map(normalizeCampaign)
          .sort(
            (a, b) =>
              new Date(b.scheduled_date || b.date || 0).getTime() -
              new Date(a.scheduled_date || a.date || 0).getTime()
          );
        console.log("✅ Normalized and sorted", normalizedCampaigns.length, "campaigns");
        setCampaigns(normalizedCampaigns);
      } catch (error) {
        console.error("❌ Dashboard campaign load failed:", error);
        setCampaigns([]);
      }
      finally { setUserLoading(false); setLoading(false); }
    };
    load();
  }, [extractCampaignList, normalizeCampaign]);

  const handleRegisterMobileToggle = useCallback((fn: () => void) => {
    setMobileMenuToggle(() => fn);
  }, []);

  // ─────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────
  return (
    <div className="flex h-screen w-full bg-white overflow-hidden">
      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      <div className={cn(
        "fixed inset-y-0 left-0 z-50 transform transition-transform duration-300 ease-in-out lg:relative lg:translate-x-0",
        "h-screen w-68 bg-white border-r border-gray-200 overflow-y-auto",
        sidebarOpen ? "translate-x-0" : "-translate-x-full"
      )}>
        <Sidebar registerMobileToggle={handleRegisterMobileToggle} />
      </div>

      <main className="flex-1 flex flex-col overflow-hidden min-w-0">
        <PageHeader
          // title="Dashboard"
          // description="Create your marketing launch in simple steps"
          mobileMenuOffset={true}
          onMenuClick={mobileMenuToggle ?? undefined}
        />

        <div className="flex-1 overflow-y-auto">
          <div className="p-4 sm:p-6 lg:p-8">
            <div className="max-w-7xl mx-auto space-y-8">

              {/* ══════ WIZARD SECTION ══════════════════════════════════════ */}
              <section>
                {/* Welcome */}
                <div className="mb-5">
                  <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">
                    Welcome {userLoading ? "..." : firstName}!
                  </h1>
                  <p className="text-gray-500 text-sm sm:text-base">Launch smarter, faster, and better.</p>
                </div>

                {/* Heading */}
                <div className="flex items-center gap-2 mb-5">
                  <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-red-100 flex-shrink-0">
                    <Target className="w-4 h-4 text-red-600" />
                  </span>
                  <span className="text-lg sm:text-xl font-bold text-gray-900">Smart Start</span>
                </div>

                {/* ── CAROUSEL ──────────────────────────────────────────── */}
                {/*
                  Structure:
                    [disabled-or-active LEFT arrow]
                    [overflow-hidden track  ← ref here]
                    [disabled-or-active RIGHT arrow]

                  Cards are sized to fill the track width exactly.
                  The track uses overflow-hidden for the slide effect.
                  pt-6 inside the track gives headroom for number badges.
                */}
                <div className="flex items-start gap-2 sm:gap-3 w-full">

                  {/* LEFT ARROW */}
                  <div className="flex-shrink-0 self-center" style={{ paddingTop: 24 }}>
                    <button
                      onClick={goPrev}
                      disabled={wizardStep === 0}
                      aria-label="Previous step"
                      className={cn(
                        "w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-white border border-gray-300 shadow-md",
                        "flex items-center justify-center transition-all duration-200 focus:outline-none",
                        wizardStep === 0 ? "opacity-25 cursor-not-allowed" : "hover:bg-gray-50 hover:scale-110"
                      )}
                    >
                      <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                    </button>
                  </div>

                  {/* TRACK — ResizeObserver target */}
                  <div className="flex-1 overflow-hidden pt-6 min-w-0" ref={trackRef}>
                    <div
                      className="flex transition-transform duration-500 ease-out"
                      style={{ gap: GAP, transform: `translateX(-${wizardStep * STEP_MOVE}px)` }}
                    >
                      {WIZARD_STEPS.map((step) => (
                        <WizardStepCard key={step.number} step={step} cardWidth={cardWidth} />
                      ))}
                    </div>
                  </div>

                  {/* RIGHT ARROW */}
                  <div className="flex-shrink-0 self-center" style={{ paddingTop: 24 }}>
                    <button
                      onClick={goNext}
                      disabled={wizardStep >= MAX_STEP}
                      aria-label="Next step"
                      className={cn(
                        "w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-white border border-gray-300 shadow-md",
                        "flex items-center justify-center transition-all duration-200 focus:outline-none",
                        wizardStep >= MAX_STEP ? "opacity-25 cursor-not-allowed" : "hover:bg-gray-50 hover:scale-110"
                      )}
                    >
                      <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                    </button>
                  </div>
                </div>

                {/* ── DOT INDICATORS ────────────────────────────────────── */}
                {/* Each dot = one slide position; clicking jumps directly */}
                <div className="flex justify-center gap-1.5 mt-4">
                  {Array.from({ length: MAX_STEP + 1 }).map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setWizardStep(i)}
                      aria-label={`Slide ${i + 1}`}
                      className={cn(
                        "rounded-full transition-all duration-300 focus:outline-none",
                        i === wizardStep ? "w-6 h-2 bg-blue-600" : "w-2 h-2 bg-gray-300 hover:bg-gray-400"
                      )}
                    />
                  ))}
                </div>

                {/* ── START CREATING CTA ────────────────────────────────── */}
                <div className="mt-6">
                  <Link to="/builder">
                    <button className="
                      h-12 px-8 w-full max-w-[260px]
                      flex items-center justify-center gap-2
                      rounded-full bg-[#ff1f1f] text-white text-sm sm:text-base font-semibold
                      shadow-sm hover:bg-[#e51b1b] transition-colors duration-200
                    ">
                      Start Creating <span className="text-lg leading-none">›</span>
                    </button>
                  </Link>
                </div>
              </section>

              {/* ══════ STATS SECTION ═══════════════════════════════════════ */}
              <section>
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-5">
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-red-100 flex-shrink-0">
                      <BarChart3 className="w-4 h-4 text-red-600" />
                    </span>
                    <span className="text-lg sm:text-xl font-bold text-gray-900">{periodText} at a Glance</span>
                  </div>

                  <Select value={timeRange} onValueChange={(v) => setTimeRange(v as any)}>
                    <SelectTrigger className="w-full sm:w-36 bg-white border border-gray-300 rounded-lg shadow-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="30d">30 Days</SelectItem>
                      <SelectItem value="thisWeek">This Week</SelectItem>
                      <SelectItem value="lastWeek">Last Week</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-5">
                  {[
                    { label: "Launch Sent", value: periodStats.campaignsSent, data: chartData.sent, color: "#3B82F6", fmt: false },
                    { label: "Launch Remaining", value: user?.campaigns_remaining ?? 0, data: remainingChartData, color: "#A78BFA", fmt: false },
                    { label: "Number of Recipients", value: periodStats.hubs, data: chartData.hubs, color: "#14B8A6", fmt: true },
                    { label: "Number of Clicks", value: periodStats.clicks, data: chartData.clicks, color: "#10B981", fmt: true },
                  ].map(({ label, value, data, color, fmt }) => (
                    <Card key={label} className="bg-white rounded-2xl border border-gray-200 overflow-hidden transition-all duration-300 hover:shadow-xl hover:-translate-y-1 hover:scale-[1.02]">
                      <CardContent className="p-4 sm:p-5">
                        <p className="text-xs sm:text-sm text-gray-500 mb-1">{label}</p>
                        <p className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
                          {loading ? <Skeleton className="h-8 w-20 mt-1" /> : fmt ? (value as number).toLocaleString() : value}
                        </p>
                        <div className="h-16 sm:h-20 -mx-4 sm:-mx-5 -mb-4 sm:-mb-5">
                          <DynamicAreaChart data={data} color={color} />
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </section>

              {/* ══════ RECENT LAUNCHES + SMART ACTIONS ═════════════════════ */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 pb-8">
                {/* Recent Launches */}
                <Card className="bg-white rounded-xl p-5 sm:p-6 flex flex-col">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-red-100 flex-shrink-0">
                      <Clock className="w-4 h-4 text-[#ff1f1f]" />
                    </span>
                    <h3 className="text-sm sm:text-base font-semibold text-gray-900">Your Recent Launches</h3>
                  </div>
                  <div className="flex-1 space-y-2 overflow-y-auto max-h-96">
                    {recentCampaigns.length > 0 ? (
                      recentCampaigns.map((c) => {
                        const isService = c.campaign_type?.toLowerCase() === "service";
                        const isSent = c.status?.toLowerCase() === "sent";
                        const id = c.id || c.property_id;
                        
                        return (
                          <div key={id} className="rounded-lg border border-gray-100 bg-gray-50 p-3 hover:bg-gray-100 transition-colors">
                            {/* Row 1: Name + Type badge + Status */}
                            <div className="flex items-start justify-between gap-2 mb-2">
                              <div className="flex-1 min-w-0">
                                <p className="font-semibold text-gray-900 text-sm truncate" title={getCampaignName(c)}>
                                  {getCampaignName(c)}
                                </p>
                              </div>
                              <div className="flex gap-1 flex-shrink-0">
                                <Badge
                                  variant="outline"
                                  className={`text-xs px-2 py-0.5 whitespace-nowrap ${isService ? "border-purple-200 bg-purple-50 text-purple-700" : "border-blue-200 bg-blue-50 text-blue-700"}`}
                                >
                                  {isService ? "Service" : c.campaign_type || "Property"}
                                </Badge>
                                <Badge className={`text-xs px-2 py-0.5 font-medium whitespace-nowrap ${getStatusBadgeClass(c.status)}`}>
                                  {getStatusLabel(c.status)}
                                </Badge>
                              </div>
                            </div>
                            
                            {/* Row 2: Date + Recipients */}
                            <div className="flex items-center gap-2 text-xs text-gray-600 mb-1.5 flex-wrap">
                              <span className="flex items-center gap-1">
                                📅 {formatDateTimeInTimezone(c.scheduled_date || c.date, c.scheduled_timezone || timezone)}
                              </span>
                              <span className="text-gray-400">·</span>
                              <span className="flex items-center gap-1">
                                👥 {getCampaignRecipients(c).toLocaleString()} recipients
                              </span>
                            </div>
                            
                            {/* Row 3: Opens/Clicks (if sent) */}
                            {isSent && (
                              <div className="flex items-center gap-2 text-xs text-gray-600">
                                <span>👁 {getCampaignOpenCount(c).toLocaleString()} opens</span>
                                <span className="text-gray-400">·</span>
                                <span>🖱 {getCampaignClickCount(c).toLocaleString()} clicks</span>
                              </div>
                            )}
                          </div>
                        );
                      })
                    ) : (
                      <div className="flex flex-col items-center justify-center py-10 text-center">
                        <Mail className="w-10 h-10 text-gray-300 mb-3" />
                        <p className="text-gray-600 font-medium mb-1 text-sm">No launches yet</p>
                        <p className="text-gray-400 text-xs mb-4">Create your first launch to get started</p>
                        <button onClick={() => navigate("/builder")}
                          className="px-5 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white text-sm font-medium rounded-xl transition-all hover:shadow-lg hover:-translate-y-0.5">
                          Create Launch
                        </button>
                      </div>
                    )}
                  </div>
                </Card>

                {/* Smart Actions */}
                <Card className="bg-white rounded-xl p-5 sm:p-6 flex flex-col">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-red-100 flex-shrink-0">
                      <Sparkles className="w-4 h-4 text-red-600" />
                    </span>
                    <h3 className="text-sm sm:text-base font-semibold text-gray-900">Your Smart Actions</h3>
                  </div>
                  <div className="flex-1 space-y-3">
                    {SMART_ACTIONS.map((item) => {
                      const locked = item.requiresPro && !hasProAccess;
                      return (
                        <button key={item.label}
                          onClick={() => locked ? setShowUpgradeModal(true) : navigate(item.route)}
                          className={cn(
                            "w-full flex items-center justify-between rounded-xl px-4 py-3 transition-colors",
                            locked ? "bg-gray-50 opacity-70 cursor-not-allowed" : "bg-gray-50 hover:bg-gray-100"
                          )}
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-blue-100 flex-shrink-0">
                              <item.icon className="w-4 h-4 text-blue-600" />
                            </span>
                            <span className="font-medium text-gray-900 text-sm truncate">{item.label}</span>
                            {locked && <Lock className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />}
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                            {locked && <Badge className="bg-amber-100 text-amber-800 text-xs">Pro</Badge>}
                            <ChevronRight className="w-4 h-4 text-gray-400" />
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </Card>
              </div>

            </div>
          </div>
        </div>

        {/* <CallingBot />
        <Chatbot /> */}
        <BotLauncher/>
      </main>

      {/* Notification Modal */}
      <Dialog open={showNotificationOptInModal} onOpenChange={setShowNotificationOptInModal}>
        <DialogContent className="sm:max-w-md mx-4 sm:mx-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-blue-600" /> Enable Notifications
            </DialogTitle>
            <DialogDescription className="pt-2 text-gray-600">
              You're not receiving updates from your MyListiq account yet.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2 text-sm text-gray-700">
            <p>To start getting notifications, 📲 send an <strong>OPT-IN</strong> message to:</p>
            <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-center text-lg font-bold text-blue-700">+1 321-519-4247</div>
            <p>Send "START" to opt in.</p>
            <p>Send "STOP" to opt out.</p>
            <p>Stay informed in real time. You can opt out anytime.</p>
          </div>
          <DialogFooter>
            <Button onClick={() => setShowNotificationOptInModal(false)} className="w-full bg-blue-600 hover:bg-blue-700 sm:w-auto">Got it</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upgrade Modal */}
      <Dialog open={showUpgradeModal} onOpenChange={setShowUpgradeModal}>
        <DialogContent className="sm:max-w-md mx-4 sm:mx-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-4 h-4 text-amber-500" /> Upgrade Required
            </DialogTitle>
            <DialogDescription className="pt-2">
              Insights & Analytics is available on <strong>Pro</strong> or <strong>Pro+</strong> plans.
            </DialogDescription>
          </DialogHeader>
          <div className="py-3">
            <div className="bg-gradient-to-r from-teal-50 to-blue-50 rounded-lg p-4 border border-teal-100">
              <h4 className="font-medium text-gray-900 mb-2 text-sm">What you'll unlock:</h4>
              <ul className="space-y-1.5 text-sm text-gray-600">
                {["Advanced performance tracking", "AI-powered insights", "Detailed analytics dashboard"].map((f) => (
                  <li key={f} className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-teal-600 flex-shrink-0" />{f}
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowUpgradeModal(false)} className="w-full sm:w-auto">Maybe Later</Button>
            <Button onClick={() => navigate("/settings?tab=subscription")} className="bg-teal-600 hover:bg-teal-700 w-full sm:w-auto">Upgrade Plan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
